import { FormEvent } from 'react'
import { Page, PageHeader } from '@/components/page'
import { useForm } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { InternationalPhoneInput } from '@/components/ui/international-phone-input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { FieldInputs, type FieldDefinition } from '@/components/applicant/FieldInputs'
import { useBasePath } from '@/lib/paths'
import { ArrowRight, Building2, Mail, ShieldCheck } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'
import type { WizardFormationRef } from '@/types'

type OwnBusinessAddress = {
  street_address_1: string
  street_address_2: string | null
  city: string
  state_province: string | null
  postal_code: string | null
  country_code: string
}

type Props = {
  formation: WizardFormationRef & { requires_us_address: boolean }
  business_description: string | null
  online_presence_kind: 'website' | 'social' | 'app' | 'prelaunch' | null
  online_presence_url: string | null
  primary_contact_email: string | null
  primary_contact_phone: string | null
  business_address_provider: 'own' | 'corppy' | null
  business_address: OwnBusinessAddress | null
  fields: FieldDefinition[]
  field_values: Record<string, string | number | string[]>
}

const PRESENCE_OPTIONS: { value: 'website' | 'social' | 'app' | 'prelaunch'; label: string; hint: string }[] = [
  { value: 'website', label: 'Website', hint: 'A URL people can visit' },
  { value: 'social', label: 'Social profile', hint: 'A social media page' },
  { value: 'app', label: 'App', hint: 'An app store or app link' },
  { value: 'prelaunch', label: "Not online yet", hint: "We're pre-launch" },
]

export default function CompaniesEdit({
  formation, business_description, online_presence_kind, online_presence_url,
  primary_contact_email, primary_contact_phone, business_address_provider, business_address,
  fields, field_values,
}: Props) {
  const basePath = useBasePath()
  const isForeign = formation.requires_us_address
  const ownAddress = business_address_provider === 'own' ? business_address : null

  const form = useForm({
    business_description: business_description ?? '',
    online_presence_kind: online_presence_kind ?? '',
    online_presence_url: online_presence_url ?? '',
    primary_contact_email: primary_contact_email ?? '',
    primary_contact_phone: primary_contact_phone ?? '',
    provider: isForeign ? 'corppy' : (business_address_provider ?? 'own'),
    street_address_1: ownAddress?.street_address_1 ?? '',
    street_address_2: ownAddress?.street_address_2 ?? '',
    city: ownAddress?.city ?? '',
    state_province: ownAddress?.state_province ?? '',
    postal_code: ownAddress?.postal_code ?? '',
    country_code: ownAddress?.country_code ?? 'US',
    field_values: field_values ?? {},
  })

  const presenceKind = form.data.online_presence_kind
  const needsUrl = presenceKind === 'website' || presenceKind === 'social' || presenceKind === 'app'

  const setFieldValue = (fieldId: string, value: string | number | string[]) => {
    form.setData('field_values', { ...form.data.field_values, [fieldId]: value })
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`${basePath}/formations/${formation.id}/wizard/company`)
  }

  return (
    <WizardLayout
      title="About your company"
      currentStep="company"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'About your company' }]}
      guide={
        <WizardGuide title="About your company">
          <p>
            A plain description of what you do helps us file correctly. Corppy is your registered agent
            either way — we receive legal notices and forward them to you.
          </p>
        </WizardGuide>
      }
      tips={
        <Tip seed="company-address-choice">
          Your business address is where the state sends official mail. It doesn't have to be where you work.
        </Tip>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <Page>
          <PageHeader
            title={<>About your company</>}
            subtitle={<>Tell us what your company does, how to reach it, then choose where it receives official mail.</>}
          />

          <div className="space-y-1.5 max-w-2xl">
            <Label htmlFor="business_description">What does your company do?</Label>
            <Textarea
              id="business_description"
              value={form.data.business_description}
              onChange={(e) => form.setData('business_description', e.target.value)}
              placeholder="e.g. We build software that helps small shops track inventory."
              rows={4}
              aria-invalid={!!form.errors.business_description}
            />
            <p className="text-xs text-muted-foreground">
              One or two sentences is enough — we use this to check your business against filing rules.
            </p>
            {form.errors.business_description && (
              <p className="text-sm text-destructive" role="alert">{form.errors.business_description}</p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Where can people find your company online?</p>
            <RadioGroup
              value={form.data.online_presence_kind}
              onValueChange={(v) => form.setData('online_presence_kind', v)}
              aria-invalid={!!form.errors.online_presence_kind}
              className="gap-2"
            >
              {PRESENCE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-start gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value={opt.value} id={`presence-${opt.value}`} className="mt-0.5" />
                  <span>
                    <span className="block font-medium">{opt.label}</span>
                    <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
            {form.errors.online_presence_kind && (
              <p className="text-sm text-destructive" role="alert">{form.errors.online_presence_kind}</p>
            )}

            {needsUrl && (
              <div className="space-y-1.5 max-w-2xl">
                <Label htmlFor="online_presence_url">Link</Label>
                <Input
                  id="online_presence_url"
                  type="url"
                  value={form.data.online_presence_url}
                  onChange={(e) => form.setData('online_presence_url', e.target.value)}
                  placeholder="https://example.com"
                  aria-invalid={!!form.errors.online_presence_url}
                />
                {form.errors.online_presence_url && (
                  <p className="text-sm text-destructive" role="alert">{form.errors.online_presence_url}</p>
                )}
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
            <div className="space-y-1.5">
              <Label htmlFor="primary_contact_email">Contact email</Label>
              <Input
                id="primary_contact_email"
                type="email"
                value={form.data.primary_contact_email}
                onChange={(e) => form.setData('primary_contact_email', e.target.value)}
                aria-invalid={!!form.errors.primary_contact_email}
              />
              {form.errors.primary_contact_email && (
                <p className="text-sm text-destructive" role="alert">{form.errors.primary_contact_email}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="primary_contact_phone">Contact phone</Label>
              <InternationalPhoneInput
                id="primary_contact_phone"
                value={form.data.primary_contact_phone}
                onValueChange={(primary_contact_phone) => form.setData('primary_contact_phone', primary_contact_phone)}
                defaultCountryCode={form.data.country_code}
                ariaInvalid={!!form.errors.primary_contact_phone}
                ariaDescribedBy={form.errors.primary_contact_phone ? 'primary_contact_phone-error' : undefined}
              />
              {form.errors.primary_contact_phone && (
                <p id="primary_contact_phone-error" className="text-sm text-destructive" role="alert">{form.errors.primary_contact_phone}</p>
              )}
            </div>
          </div>

          {fields.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Filing details</p>
              <FieldInputs
                fields={fields}
                values={form.data.field_values}
                onChange={setFieldValue}
                errors={form.errors as unknown as Record<string, string>}
              />
            </div>
          )}

          <div className="rounded-lg border bg-muted/40 p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck className="size-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-foreground">Registered agent</p>
                <p className="text-sm text-muted-foreground">
                  Corppy is your registered agent — included with every formation. We receive your legal
                  notices and forward them to you.
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-foreground mb-2">Business address</p>
            {isForeign ? (
              <div className="flex items-start gap-3 rounded-lg border bg-muted/40 p-5">
                <Mail className="size-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">
                  Since you're filing from outside the US, Corppy provides your US business address and mail
                  handling — included with your formation.
                </p>
              </div>
            ) : (
              <div>
                <RadioGroup
                  value={form.data.provider}
                  onValueChange={(value) => form.setData('provider', value as 'own' | 'corppy')}
                  aria-label="Business address"
                  aria-invalid={!!form.errors.provider}
                  className="grid gap-3 sm:grid-cols-2"
                >
                  <label
                    htmlFor="address-own"
                    className="group flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
                  >
                    <RadioGroupItem value="own" id="address-own" className="mt-1" />
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Building2 className="size-4 text-muted-foreground" aria-hidden="true" /> Use my own address
                      </span>
                      <span className="text-xs text-muted-foreground">A home, office, or other US address you control.</span>
                    </span>
                  </label>
                  <label
                    htmlFor="address-corppy"
                    className="group flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
                  >
                    <RadioGroupItem value="corppy" id="address-corppy" className="mt-1" />
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Mail className="size-4 text-muted-foreground" aria-hidden="true" /> Use Corppy's address
                      </span>
                      <span className="text-xs text-muted-foreground">Bundled with your registered agent service.</span>
                    </span>
                  </label>
                </RadioGroup>
                {form.errors.provider && (
                  <p className="text-sm text-destructive mt-1.5" role="alert">{form.errors.provider}</p>
                )}

                {form.data.provider === 'own' && (
                  <div className="space-y-4 mt-4 max-w-md">
                    <div className="space-y-1.5">
                      <Label htmlFor="street_address_1">Street address</Label>
                      <Input
                        id="street_address_1"
                        value={form.data.street_address_1}
                        onChange={(e) => form.setData('street_address_1', e.target.value)}
                        aria-invalid={!!form.errors.street_address_1}
                      />
                      {form.errors.street_address_1 && (
                        <p className="text-sm text-destructive" role="alert">{form.errors.street_address_1}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="street_address_2">
                        Suite / unit <span className="text-muted-foreground font-normal">(optional)</span>
                      </Label>
                      <Input
                        id="street_address_2"
                        value={form.data.street_address_2}
                        onChange={(e) => form.setData('street_address_2', e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="city">City</Label>
                        <Input id="city" value={form.data.city} onChange={(e) => form.setData('city', e.target.value)} aria-invalid={!!form.errors.city} />
                        {form.errors.city && <p className="text-sm text-destructive" role="alert">{form.errors.city}</p>}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="state_province">State</Label>
                        <Input
                          id="state_province"
                          value={form.data.state_province}
                          onChange={(e) => form.setData('state_province', e.target.value)}
                          aria-invalid={!!form.errors.state_province}
                        />
                        {form.errors.state_province && (
                          <p className="text-sm text-destructive" role="alert">{form.errors.state_province}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5 max-w-[160px]">
                      <Label htmlFor="postal_code">ZIP code</Label>
                      <Input
                        id="postal_code"
                        value={form.data.postal_code}
                        onChange={(e) => form.setData('postal_code', e.target.value)}
                        aria-invalid={!!form.errors.postal_code}
                      />
                      {form.errors.postal_code && (
                        <p className="text-sm text-destructive" role="alert">{form.errors.postal_code}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <WizardActions currentStep="company" formationId={formation.id} disabled={form.processing}>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Continue'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
        </Page>
      </form>
    </WizardLayout>
  )
}
